import type { ReactNode } from 'react'

import { cn } from '@gto/ui/cn'
import { spacing } from '@gto/ui/theme/design-tokens'

import { DashboardSlideArtwork } from './dashboard-slide-artwork'
import { DashboardSlideText } from './dashboard-slide-text'
import { DashboardSlideTitle } from './dashboard-slide-title'
import { SlideCountdown } from './slide-countdown'
import { useCountdown } from './use-countdown'

/**
 * The dark viewport a dashboard slide is painted on. Exported rather than baked
 * into `DashboardSlide` because in the app it belongs to the carousel — one
 * surface behind every slide, and the element that clips the cross-fade — while
 * the admin preview has no carousel and paints it on its own wrapper. Sharing
 * the constant is what stops the two from drifting apart.
 *
 * The primary tint is not decoration for its own sake: this surface is what a
 * viewer actually looks at until the artwork arrives, and below the artwork's
 * breakpoint it is the whole slide forever. Flat grey read as a picture that had
 * failed to load. The gradient runs the tint down from the copy on the left
 * toward the right, where artwork sits when there is any, so it recedes rather
 * than competing once the picture lands.
 */
export const DASHBOARD_SLIDE_SURFACE_CLASS =
  'min-h-[230px] overflow-hidden rounded-[8px] bg-paper-elevation3 bg-gradient-primary-horizontal'

/**
 * Width the dashboard slide is authored at, in px. Read off the design token
 * rather than written again here, the way `modalDesignWidth` reads the modal
 * steps. The admin preview scales its stage to this number, so a second copy is
 * a second place the preview can drift away from the shipped slide. The token
 * carries the derivation of the number itself.
 */
export const DASHBOARD_SLIDE_DESIGN_WIDTH = Number.parseInt(
  spacing['announcement-dashboard-slide'],
  10,
)

/**
 * The height that width is authored against, in px. It is a MINIMUM, not a fixed
 * height: a wrapped headline or a stacked countdown makes the real slide taller.
 * `DASHBOARD_SLIDE_SURFACE_CLASS` above states the same 230 as a Tailwind class,
 * and the two must stay equal — the surface is what this slide has to cover.
 *
 * Load-bearing, not decoration: the artwork is positioned against this element, so
 * a slide allowed to shrink under the surface's own 230px would stop covering it
 * and band the gradient below the picture. A style rather than a Tailwind class
 * because 230 sits off the 4px spacing scale, and the design-system ratchet counts
 * an arbitrary-pixel class as drift — so the class form would trade one tracked
 * metric for another and net nothing.
 */
const SLIDE_MIN_HEIGHT = 230

export type DashboardSlideProps = {
  /** Headline — a `string[]` stacks each entry as its own two-tone row. */
  title: string | string[]
  /** Renders the headline at 24px/30px instead of the carousel's historical 32px. */
  compactTitle?: boolean
  /** Authored headline size in px; overrides both the default and `compactTitle`. */
  titleSizePx?: number
  /** Body — a `string[]` renders a bulleted list, a `string` splits on `<br />`. */
  text: string | string[]
  /** Artwork URL. An empty string leaves the slide on the bare surface colour. */
  image: string
  /**
   * Fetch this slide's artwork ahead of the other slides'. Set it on whichever
   * slide is on screen first — the carousel mounts them all at once, so with no
   * order stated the browser splits bandwidth across pictures nobody is looking
   * at yet, and the visible one is the last to arrive.
   */
  priority?: boolean
  /**
   * Artwork that fails to load leaves the slide silently picture-less forever, so
   * somebody has to hear about it. Reported by the caller rather than here, which
   * is how this stays a presentation component: the app wires its own channel, and
   * the admin preview leaves it unset because a staging operator is already looking
   * straight at the result.
   */
  onArtworkError?: () => void
  /**
   * Deadline for the countdown tiles, as epoch milliseconds. Undefined renders no
   * tiles, which is every slide that has not authored one.
   */
  countdownTargetMs?: number
  /** Caption above the tiles; ignored without `countdownTargetMs`. */
  countdownLabel?: string
  /** Keeps expired tiles on screen. The admin preview's case — see `SlideCountdown`. */
  showExpiredCountdown?: boolean
  /**
   * The CTA row's buttons. Passed in rather than derived from links so the app
   * keeps its analytics, SPA routing and hover wiring on them, and the preview
   * can render inert ones — while the row's own geometry stays shared.
   */
  actions?: ReactNode
}

/**
 * One slide of the dashboard announcement carousel, exactly as customers see it.
 *
 * Presentation only: no analytics, no routing, no carousel state. That split is
 * what lets the admin preview render the real thing (C-1840) without dragging
 * the customer app's providers into a staff tool — the objection that kept the
 * preview a hand-drawn likeness until now.
 */
export const DashboardSlide = ({
  title,
  compactTitle,
  titleSizePx,
  text,
  image,
  priority,
  onArtworkError,
  countdownTargetMs,
  countdownLabel,
  showExpiredCountdown,
  actions,
}: DashboardSlideProps) => {
  const countdown = useCountdown(countdownTargetMs)
  // ONE reading drives the tiles, the wrapper AND the narrow-screen stacking.
  // Keying the layout on `countdownTargetMs` alone instead left an expired slide
  // stacked around an empty padded column: the deadline is still a number, the
  // tiles are gone.
  //
  // A passed deadline hides the tiles for a customer; the admin preview asks to
  // keep them, because an operator inspecting a finished campaign has to see what
  // they authored. Which announcements survive the deadline at all is decided
  // upstream, by the selector reading `countdown_expiry`.
  const showsCountdown =
    countdownTargetMs !== undefined && (!countdown.isExpired || showExpiredCountdown === true)

  return (
    // `overflow-hidden` earns its place now that the artwork is a child element
    // rather than a background: a border-radius clips a background on its own, but
    // it does not clip a positioned child, and the corners would square off.
    <div
      style={{ minHeight: SLIDE_MIN_HEIGHT }}
      className={cn(
        'relative flex w-full items-center gap-8 overflow-hidden rounded-[8px]',
        // Stacking is conditional so a slide with no countdown keeps the single-row
        // layout it has always had on narrow screens. Side by side, the tiles and
        // the copy would each get half a phone's width.
        showsCountdown && 'md:flex-col md:items-stretch md:gap-4 md:py-4',
      )}
    >
      {/* An imageless slide is an authored choice, so it renders no artwork element
          at all rather than an empty one that would still cost a request. */}
      {image !== '' && (
        <DashboardSlideArtwork
          priority={priority}
          src={image}
          onError={onArtworkError}
        />
      )}
      <div className="relative z-1 flex flex-1 flex-col gap-4 px-8 text-white">
        <DashboardSlideTitle
          compact={compactTitle}
          sizePx={titleSizePx}
          text={title}
        />
        <DashboardSlideText text={text} />
        {actions !== undefined && (
          <div className="flex flex-row items-center gap-4 self-start">{actions}</div>
        )}
      </div>
      {showsCountdown && (
        <div className="relative z-1 pr-8 md:px-8">
          <SlideCountdown
            countdown={countdown}
            label={countdownLabel}
          />
        </div>
      )}
    </div>
  )
}
